// Кнопка «прикрепить файл» в композере (ADR-115).

import { useRef } from 'react'
import type { ChangeEvent } from 'react'
import { Icon } from '@/components/icon/Icon'
import { cn } from '@/lib/utils'
import { useI18n } from '@/lib/i18n'
import { buildAttachmentAccept } from '@/lib/attachments/accept'
import { useDevice } from '@/hooks/useDevice'
import { useAttachmentsStore } from '@/stores/attachmentsStore'

export interface AttachmentButtonProps {
  sessionId: string
  disabled?: boolean
  /**
   * Готовый путь подготовки файлов (ADR-115). Композер передаёт сюда
   * `attachFiles` — подготовка (MIME, HEIC, office-группы) и стор живут в
   * одном месте. Без пропа кнопка работает автономно: кладёт файлы в стор
   * напрямую, без подготовки.
   */
  onFiles?: (files: File[]) => void | Promise<void>
}

/**
 * Кнопка со скрытым `<input type="file" multiple>`.
 *
 * Меню выбора источника (файлы/контекст) пока не нужно: на телефоне и на
 * десктопе работает один и тот же системный диалог (ADR-044).
 */
export function AttachmentButton({
  sessionId,
  disabled = false,
  onFiles,
}: AttachmentButtonProps) {
  const { t } = useI18n()
  const { deviceType } = useDevice()
  const inputRef = useRef<HTMLInputElement>(null)
  const addFiles = useAttachmentsStore((state) => state.addFiles)
  // Лимиты инстанса: `accept` фильтруется по разрешённым семействам,
  // при выключенных вложениях кнопка блокируется.
  const limits = useAttachmentsStore((state) => state.limits)
  const disabledByServer = !limits.enabled
  const isDisabled = disabled || disabledByServer
  const label = disabledByServer ? t('chat.attachments.disabled') : t('chat.attachments.attach')

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? [])
    // Сбрасываем значение: повторный выбор того же файла должен снова срабатывать.
    event.target.value = ''
    if (files.length === 0) return
    if (onFiles) {
      void onFiles(files)
      return
    }
    void addFiles(sessionId, files)
  }

  return (
    <>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={isDisabled}
        data-device={deviceType}
        aria-label={label}
        title={label}
        className={cn(
          'inline-flex size-8 items-center justify-center rounded-lg text-muted-foreground',
          'transition-colors hover:bg-interactive-hover hover:text-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          'disabled:cursor-not-allowed disabled:opacity-40 pointer-coarse:size-10',
        )}
      >
        <Icon name="attachment-2" className="size-4" />
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={buildAttachmentAccept(limits.modalities)}
        className="hidden"
        tabIndex={-1}
        aria-hidden="true"
        data-testid="attachment-input"
        onChange={handleChange}
      />
    </>
  )
}
